import { useState, useEffect } from "react";
import {
  getExperienceData,
  uploadWorkExperience,
  deleteWorkExperience,
  updateWorkExperience,
} from "../../../service/api";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import "bootstrap/dist/css/bootstrap.min.css";
import { toast } from "react-toastify";
import Swal from "sweetalert2";

function WorkExperience() {
  const [showModal, setShowModal] = useState(false);
  const [expBtnLoading, setExpBtnLoading] = useState(false);
  const [loading, setLoading] = useState(false);
  const [experience, setExperience] = useState([]);
  const [sessionFrom, setSessionFrom] = useState(null);
  const [sessionTo, setSessionTo] = useState(null);
  const [editingExp, setEditingExp] = useState(null);

  // Fetch experiences
  useEffect(() => {
    const fetchExperience = async () => {
      try {
        setLoading(true);
        const response = await getExperienceData();
        setExperience(response.data);
      } catch (err) {
        console.error("Error fetching work experience:", err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchExperience();
  }, []);

  const parseDate = (dateStr) => {
    if (!dateStr) return null;

    // If it's ISO or standard parsable format
    const iso = Date.parse(dateStr);
    if (!isNaN(iso)) {
      return new Date(iso);
    }

    // If it's dd-MM-yyyy
    const parts = dateStr.split("-");
    if (parts.length === 3) {
      const day = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // month is 0-based
      const year = parseInt(parts[2], 10);
      return new Date(year, month, day);
    }

    return null;
  };

  const openModal = (exp = null) => {
    if (exp) {
      setEditingExp(exp);

      const fromDate = parseDate(exp.sessionFrom);
      const toDate = parseDate(exp.sessionTo);

      setSessionFrom(fromDate);
      setSessionTo(toDate);
    } else {
      setEditingExp(null);
      setSessionFrom(null);
      setSessionTo(null);
    }
    setShowModal(true);
  };

  // Handle submit (Add/Edit)
  const handleExperienceSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!sessionFrom || !sessionTo) {
        toast.error("Please select valid session dates");
        return;
      }
      if (sessionTo <= sessionFrom) {
        toast.error("Session To must be greater than Session From");
        return;
      }

      setExpBtnLoading(true);
      const form = e.target;
      const formatDate = (date) => {
        if (!date) return null;
        const d = new Date(date);
        const day = String(d.getDate()).padStart(2, "0");
        const month = String(d.getMonth() + 1).padStart(2, "0"); // month is 0-based
        const year = d.getFullYear();
        return `${year}-${month}-${day}`; // yyyy-MM-dd
      };

      const data = {
        experienceId: editingExp ? editingExp.id : null,
        jobTitle: form.jobTitle.value,
        companyName: form.companyName.value,
        sessionFrom: formatDate(sessionFrom), // formatted string
        sessionTo: formatDate(sessionTo), // formatted string
        roleDescription: form.roleDescription.value,
      };

      let response;
      if (editingExp) {
        response = await updateWorkExperience(data);
      } else {
        response = await uploadWorkExperience(data);
      }

      toast.success(response.data.message);

      form.reset();
      setSessionFrom(null);
      setSessionTo(null);
      setEditingExp(null);
      setShowModal(false);

      const updatedList = await getExperienceData();
      setExperience(updatedList.data);
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || "Something went wrong"
      );
    } finally {
      setExpBtnLoading(false);
    }
  };

  // Delete experience
  const handleDeleteExperience = async (expId) => {
    try {
      const response = await deleteWorkExperience(expId);
      toast.success(response.data.message);
      setExperience((prev) => prev.filter((exp) => exp.id !== expId));
    } catch (error) {
      toast.error(
        error.response?.data?.message || error.message || "Something went wrong"
      );
    }
  };

  return (
    <div>
      
      <button className="normalbtn mb-3" onClick={() => openModal()}>
        <i className="fa-solid fa-plus"></i> Add Work Experience
      </button>

      {/* Modal */}
      {showModal && (
        <div className="modal fade show d-block" tabIndex="-1">
          <div className="modal-dialog modal-md">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  {editingExp ? "Edit Work Experience" : "Add Work Experience"}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => setShowModal(false)}
                ></button>
              </div>
              <div className="modal-body">
                <form onSubmit={handleExperienceSubmit}>
                  <div className="row hj-form">
                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label>
                          Job Role / Title{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="jobTitle"
                          placeholder="Enter Job Role e.g. Graphic Designer"
                          required
                          defaultValue={editingExp ? editingExp.jobTitle : ""}
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label>
                          Company <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          name="companyName"
                          placeholder="Enter Company Name"
                          required
                          defaultValue={
                            editingExp ? editingExp.companyName : ""
                          }
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label>
                          Session From <span className="text-danger">*</span>
                        </label>
                        <DatePicker
                          selected={sessionFrom}
                          onChange={(date) => {
                            setSessionFrom(date);
                            if (sessionTo && date && sessionTo <= date)
                              setSessionTo(null);
                          }}
                          selectsStart
                          startDate={sessionFrom}
                          endDate={sessionTo}
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Select start date"
                          required
                          showYearDropdown
                          showMonthDropdown
                          dropdownMode="select"
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <div className="col-md-6">
                      <div className="form-group mb-3">
                        <label>
                          Session To <span className="text-danger">*</span>
                        </label>
                        <DatePicker
                          selected={sessionTo}
                          onChange={(date) => setSessionTo(date)}
                          selectsEnd
                          startDate={sessionFrom}
                          endDate={sessionTo}
                          minDate={
                            sessionFrom
                              ? new Date(sessionFrom.getTime() + 86400000)
                              : null
                          }
                          dateFormat="dd/MM/yyyy"
                          className="form-control"
                          placeholderText="Select end date"
                          required
                          showYearDropdown
                          showMonthDropdown
                          dropdownMode="select"
                          autoComplete="off"
                        />
                      </div>
                    </div>

                    <div className="col-md-12">
                      <div className="form-group mb-3">
                        <label>
                          Role Description{" "}
                          <span className="text-danger">*</span>
                        </label>
                        <textarea
                          name="roleDescription"
                          className="form-control"
                          placeholder="Describe your role in this position..."
                          rows={4}
                          required
                          defaultValue={
                            editingExp ? editingExp.roleDescription : ""
                          }
                        ></textarea>
                      </div>
                    </div>

                    <div className="col-md-12">
                      <button
                        type="submit"
                        className="mt-4 normalbtn"
                        disabled={expBtnLoading}
                      >
                        {expBtnLoading ? (
                          <span>
                            <i className="fa fa-spinner fa-spin me-2"></i>
                            Submitting...
                          </span>
                        ) : editingExp ? (
                          "Update"
                        ) : (
                          "Submit"
                        )}
                      </button>
                    </div>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}

      {showModal && <div className="modal-backdrop fade show"></div>}


      <div className="row">
        {loading ? (
          <div className="text-center w-100 my-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        ) : experience.length === 0 ? (
          <p className="text-center w-100">No work experience data found.</p>
        ) : (
          experience.map((exp) => (
            <div key={exp.id} className="col-12 col-md-12 mb-4">
              <div className="exp-card">
                <h5 className="card-title">{exp.jobTitle}</h5>
                <h6 className="card-subtitle mb-2 text-muted">
                  {exp.companyName}
                </h6>
                <p>
                  Session ({exp.sessionFrom} to {exp.sessionTo})
                </p>
                <hr />
                <div className="text">{exp.roleDescription}</div>
                <div className="profile-btn">
                  <button
                    className="edit-btn"
                    onClick={() => openModal(exp)}
                  >
                    <i className="fa fa-edit"></i>
                  </button>
                  <button
                    className="delete-btn"
                    onClick={() =>
                      Swal.fire({
                        title: "Are you sure?",
                        text: "You won't be able to revert this!",
                        icon: "warning",
                        showCancelButton: true,
                        confirmButtonColor: "#d33",
                        cancelButtonColor: "#3085d6",
                        confirmButtonText: "Yes, delete it!",
                        cancelButtonText: "Cancel",
                      }).then((result) => {
                        if (result.isConfirmed) handleDeleteExperience(exp.id);
                      })
                    }
                  >
                    <i className="fa fa-trash"></i>
                  </button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>

    </div>
  );
}

export default WorkExperience;
